'use client'

import {
  FullStory,
  init as initFullStory,
  isInitialized as isFullStoryInitialized,
} from '@fullstory/browser'
import { AnalyticsBrowser, type EventProperties } from '@segment/analytics-next'
import { usePathname } from 'next/navigation'
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  type FC,
  type JSX,
  type PropsWithChildren,
} from 'react'
import AnalyticsConstants from '../../constants/analytics'
import appNames from '../../constants/appNames'
import { useCDNUrl, useWriteKey } from '../hooks/useConfig'
import { isGa4PageViewEvent, sendGa4Event } from '../lib/ga4'
import sendLog from '../lib/sendLog'
import { getErrorMessage } from '../lib/utils'

class TrackingServiceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TrackingServiceError'
  }
}

const AnalyticsContext = createContext<
  | {
      analytics: AnalyticsBrowser | undefined
      track: (event: string, properties?: EventProperties) => Promise<void>
      identify: (properties?: EventProperties) => Promise<void>
      events: Record<string, Record<string, string>>
      sessionId: string
    }
  | undefined
  | {
      sessionId: string
      track: null
      identify: null
      events: Record<string, Record<string, string>>
      analytics: null
    }
>(undefined)

type AnalyticsProviderProps = PropsWithChildren<{ sessionId: string }>

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({
  children,
  sessionId,
}): JSX.Element => {
  const [writeKey] = useWriteKey()
  const [cdnURL] = useCDNUrl()
  const pathname = usePathname()

  // Initialize FullStory once at the provider level
  useLayoutEffect(() => {
    if (
      process.env.ENABLE_FULLSTORY === 'true' &&
      process.env.FULLSTORY_ORG_ID &&
      !isFullStoryInitialized()
    ) {
      initFullStory({ orgId: process.env.FULLSTORY_ORG_ID })
    }
  }, [])

  const analytics = useMemo(() => {
    if (!writeKey) return undefined

    return AnalyticsBrowser.load({ writeKey, cdnURL })
  }, [writeKey, cdnURL])

  const identify = async (properties?: EventProperties): Promise<void> => {
    try {
      await analytics?.identify(properties)

      if (isFullStoryInitialized()) {
        FullStory('setProperties', {
          type: 'user',
          properties: properties ?? {},
        })
      }
    } catch (error) {
      sendLog(
        new TrackingServiceError(
          `Analytics.identify could not call analytics.identify: ${getErrorMessage(error)}`
        )
      )
    }
  }

  const track = async (event: string, properties?: EventProperties): Promise<void> => {
    try {
      const eventPrefix = appNames[pathname] ? `${appNames[pathname]} - ` : ''
      await analytics?.track(`${eventPrefix}${event}`, properties ?? {})

      if (isFullStoryInitialized()) {
        FullStory('trackEvent', {
          name: `${eventPrefix}${event}`,
          properties: properties ?? {},
        })
      }

      if (!isGa4PageViewEvent(event)) {
        sendGa4Event(event, properties)
      }
    } catch (error) {
      sendLog(
        new TrackingServiceError(
          `Analytics.track could not call analytics.track: ${getErrorMessage(error)}`
        )
      )
    }
  }

  const events = AnalyticsConstants.EVENTS

  if (!analytics)
    // While we wait for Segment to load, we can still return initialized variables
    return (
      <AnalyticsContext.Provider
        value={{
          sessionId,
          events,
          track: null,
          identify: null,
          analytics: null,
        }}
      >
        {children}
      </AnalyticsContext.Provider>
    )

  return (
    <AnalyticsContext.Provider
      value={{
        analytics,
        track,
        identify,
        events,
        sessionId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalytics must be used within the AnalyticsProvider')
  }

  return context
}
